import type { DataGridBand, DataGridColumn } from './types';

/**
 * Contiguous group of columns sharing the same band (or no band at all).
 * Each segment becomes one `<th colSpan={span}>` in the header band row.
 */
export interface BandSegment {
  band: DataGridBand | null;
  span: number;
}

/**
 * Walk the column list once and build the band-row segments. Consecutive
 * columns sharing the same band id collapse into a single segment; gaps
 * (columns without a band, or between two different bands) become empty
 * segments so alignment with the main header row is preserved.
 *
 * Returns `null` when no bands are defined at all — callers use that
 * as a cheap "should I render a band row ?" signal without going
 * through the segments array.
 */
export function buildBandSegments<T>(
  columns: DataGridColumn<T>[],
  bands: DataGridBand[] | undefined
): BandSegment[] | null {
  if (!bands || bands.length === 0) return null;
  const bandById = new Map(bands.map((b) => [b.id, b]));

  const segments: BandSegment[] = [];
  let current: BandSegment | null = null;
  for (const col of columns) {
    const band = col.band ? (bandById.get(col.band) ?? null) : null;
    if (current && current.band?.id === band?.id) {
      current.span += 1;
    } else {
      current = { band, span: 1 };
      segments.push(current);
    }
  }
  return segments;
}
